import { useState, useEffect } from 'react';

import type { Prescreen } from '../consumers/prescreen-consumer/prescreen-consumer';
import type { SlideUp } from '../consumers/slide-up-consumer/slide-up-consumer';
import type { Teaser } from '../consumers/teaser-consumer/teaser-consumer';
import type { Placement } from '../placements';
import type { PrescreenPayload, TeaserPayload } from '../utils/destructure';
import destructure from '../utils/destructure';
import type { TrackingLibrary } from '../utils/tracking';

export interface Advertisement {
  id?: string;
  triggerId?: string;
  isControlMessage?: boolean;
  extras: {
    placement: Placement;
    section: string;
    ignore_capping?: string;
    control?: string;
  };
}

export interface Message {
  type: string;
  data: Advertisement | Advertisement[];
}

export interface BrazeConsumerProps {
  subscribeConsumer?: (
    placement: Placement,
    handleIncomingData: (payload: any, section: string) => void
  ) => void;
  placement: Placement;
  openComponent: typeof Teaser | typeof Prescreen | typeof SlideUp;
  emptyComponent: React.FC;
  trackingLibrary?: TrackingLibrary;
  transportLayer?: any;
}

interface ComponentProps {
  payload: ReturnType<typeof destructure>;
  type: string;
  section: string;
}

export interface PublishProps {
  type: string;
  payload: {
    campaignId?: string;
    value?: any;
    key?: string;
    buttonId?: string;
  };
}

// wrapper around all consumer implementations. Keeps state in which component is currently shown and which not.
// Every child consumer component can be hidden by calling this.props.close() callback. Each consumer will be shown
// when a related message has arrived. The component below assumes that when a meesage is received, it is applicable
// for it so we show it. Currently the filtering / routing logic is done in the BrazeProvider, although a better approach would
// be probably to delegate it to each consumer since any change in the provider implementation (for example changing consumers[type].each
// to consumers.each) should not brake the child components logic.
const BrazeConsumer: React.FC<BrazeConsumerProps> = ({
  placement,
  subscribeConsumer,
  openComponent: OpenComponent,
  emptyComponent: EmptyComponent,
  trackingLibrary,
}: BrazeConsumerProps) => {
  const [componentProps, setComponentProps] = useState<Partial<ComponentProps>>(
    {}
  );
  const [open, setOpen] = useState(false);

  // incoming data sets consumer's status to opened (so it is shown) and saves the payload in order to forward
  // it to the concrete consumer (See teaser consumer).
  const handleIncomingData = (
    payload: TeaserPayload | PrescreenPayload,
    section: string
  ): void => {
    if (!open) {
      setComponentProps({
        payload: destructure(placement, payload),
        section,
      });
      setOpen(true);
    }
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  useEffect(() => {
    if (subscribeConsumer) {
      const unsubscribe = subscribeConsumer(placement, handleIncomingData);
      return unsubscribe;
    }
  }, []);

  if (open) {
    return (
      // @ts-expect-error i do not know
      <OpenComponent
        close={handleClose}
        trackingLibrary={trackingLibrary}
        {...componentProps}
      />
    );
  } else {
    return <EmptyComponent />;
  }
};

export { BrazeConsumer };
